import { useCallback, useEffect, useState } from 'react';
import { api, downloadFile, errorText, formatDate, formatDateTime, formatMoney } from '../api';
import { Card, CheckField, Empty, Loading, Notice, TextField } from '../components/ui';
import type { ArchivedDocument, Partner, TaxTreatment } from '../types';
import { TAX_TREATMENT_LABEL } from '../types';

interface AccountingSettings {
  consultant_number: number | null;
  client_number: number | null;
  account_length: number;
  skr: '03' | '04';
  revenue_account: number | null;
  revenue_account_reduced: number | null;
  revenue_account_free: number | null;
  cash_account: number | null;
  card_transit_account: number | null;
  bu_schluessel: string | null;
  tax_rate_percent: number;
  company_name: string | null;
  street: string | null;
  zip: string | null;
  city: string | null;
  country: string;
  vat_id: string | null;
  tax_number: string | null;
  email: string | null;
  phone: string | null;
  iban: string | null;
  bic: string | null;
  bank_name: string | null;
  payment_terms_days: number;
  /** § 19 UStG - dann wird auf keiner Rechnung Steuer ausgewiesen. */
  small_business: boolean;
}

interface DatevPreview {
  rows: {
    invoiceId: string;
    number: string;
    ownerName: string;
    date: string;
    debitorAccount: number | null;
    grossCents: number;
    netCents: number;
    taxCents: number;
  }[];
  problems: string[];
  totalCents: number;
}

interface ExportLog {
  id: string;
  period_start: string;
  period_end: string;
  filename: string;
  invoice_count: number;
  total_cents: number;
  created_at: string;
  created_by_name: string | null;
}

interface ZmMeldung {
  von: string;
  bis: string;
  zeilen: { vat_id: string; kunde: string; land: string; netto_cents: number; belege: number }[];
  summe_cents: number;
  ohne_ustid: { kunde: string; netto_cents: number }[];
}

const firstOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

export function AccountingPage() {
  const [settings, setSettings] = useState<AccountingSettings | null>(null);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [accounts, setAccounts] = useState<Record<string, string>>({});
  const [vatIds, setVatIds] = useState<Record<string, string>>({});
  const [treatments, setTreatments] = useState<Record<string, TaxTreatment>>({});
  const [archive, setArchive] = useState<ArchivedDocument[]>([]);
  const [exports, setExports] = useState<ExportLog[]>([]);
  const [period, setPeriod] = useState({ from: firstOfMonth(), to: new Date().toISOString().slice(0, 10) });
  const [preview, setPreview] = useState<DatevPreview | null>(null);
  const [zm, setZm] = useState<ZmMeldung | null>(null);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [loaded, partnerList, exportList, archiveList] = await Promise.all([
        api<AccountingSettings>('/api/accounting/settings'),
        api<Partner[]>('/api/partners', { query: { role: 'owner' } }),
        api<ExportLog[]>('/api/accounting/datev/exports'),
        api<ArchivedDocument[]>('/api/accounting/archive'),
      ]);
      setSettings(loaded);
      setPartners(partnerList.filter((partner) => !partner.is_self));
      setExports(exportList);
      setArchive(archiveList);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function patch(change: Partial<AccountingSettings>) {
    setSettings((current) => (current ? { ...current, ...change } : current));
  }

  async function saveSettings() {
    if (!settings) return;
    setBusy(true);
    setError('');
    try {
      await api('/api/accounting/settings', { method: 'PUT', body: settings });
      setMessage('Einstellungen gespeichert.');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function saveAccount(partner: Partner) {
    const konto = accounts[partner.id] ?? String(partner.debitor_account ?? '');
    const ustId = vatIds[partner.id] ?? partner.vat_id ?? '';
    setBusy(true);
    setError('');
    try {
      await api(`/api/accounting/partners/${partner.id}/accounts`, {
        method: 'PUT',
        body: {
          debitorAccount: konto ? Number(konto) : null,
          kreditorAccount: partner.kreditor_account ?? null,
          vatId: ustId.trim() || null,
          buyerReference: partner.buyer_reference ?? null,
          taxTreatment: treatments[partner.id] ?? partner.tax_treatment,
        },
      });
      setMessage(`Buchhaltungsdaten für ${partner.name} gespeichert.`);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function runPreview() {
    setBusy(true);
    setError('');
    setPreview(null);
    try {
      setPreview(
        await api<DatevPreview>('/api/accounting/datev/preview', {
          method: 'POST',
          body: period,
        }),
      );
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function ladeZm() {
    setBusy(true);
    setError('');
    setZm(null);
    try {
      setZm(await api<ZmMeldung>('/api/accounting/zm', { query: { von: period.from, bis: period.to } }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function runExport() {
    setBusy(true);
    setError('');
    try {
      await downloadFile('/api/accounting/datev/export', { method: 'POST', body: period });
      setMessage('Buchungsstapel erzeugt und heruntergeladen.');
      setPreview(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!settings) return <Loading />;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <Card title="DATEV-Buchungsstapel">
        <p className="small muted" style={{ marginTop: 0 }}>
          Übergeben werden ausschliesslich festgeschriebene Rechnungen, die noch in keinem Stapel
          waren. Ein Entwurf gehört nicht in die Buchhaltung, und ein zweifach übergebener Beleg
          wäre eine Doppelbuchung.
        </p>
        <div className="form-grid">
          <TextField label="Von" type="date" value={period.from} onChange={(v) => setPeriod({ ...period, from: v })} />
          <TextField label="Bis" type="date" value={period.to} onChange={(v) => setPeriod({ ...period, to: v })} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="primary" onClick={runPreview} disabled={busy}>
            Vorschau
          </button>
          {preview && preview.rows.length > 0 && preview.problems.length === 0 && (
            <button onClick={runExport} disabled={busy}>
              Stapel erzeugen und herunterladen
            </button>
          )}
        </div>

        {preview && (
          <>
            {preview.problems.length > 0 && (
              <Notice kind="error">
                <strong>Vor dem Export zu klären:</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {preview.problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              </Notice>
            )}
            {preview.rows.length === 0 ? (
              <Empty>Im Zeitraum gibt es nichts zu übergeben.</Empty>
            ) : (
              <table style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>Rechnung</th>
                    <th>Auftraggeber</th>
                    <th className="num">Debitor</th>
                    <th className="num">Netto</th>
                    <th className="num">Steuer</th>
                    <th className="num">Brutto</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => (
                    <tr key={row.invoiceId}>
                      <td className="mono">{row.number}</td>
                      <td className="small">{row.ownerName}</td>
                      <td className="num mono">{row.debitorAccount ?? '—'}</td>
                      <td className="num">{formatMoney(row.netCents)}</td>
                      <td className="num">{formatMoney(row.taxCents)}</td>
                      <td className="num">{formatMoney(row.grossCents)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'right', fontWeight: 600 }}>
                      Summe
                    </td>
                    <td className="num" style={{ fontWeight: 600 }}>
                      {formatMoney(preview.totalCents)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </>
        )}
      </Card>

      <Card title="Zusammenfassende Meldung (EU)">
        <p className="small muted" style={{ marginTop: 0 }}>
          Innergemeinschaftliche Lieferungen an Kunden mit USt-IdNr. und Steuerbehandlung „EU
          (Reverse Charge)", summiert je USt-IdNr. für den oben gewählten Zeitraum. Meldefertig — die
          Abgabe erfolgt über das BZSt-Portal oder den Steuerberater.
        </p>
        <button className="primary" onClick={ladeZm} disabled={busy}>
          Meldung berechnen
        </button>
        {zm && (
          <>
            {zm.ohne_ustid.length > 0 && (
              <Notice kind="error">
                <strong>{zm.ohne_ustid.length} Kunde(n) mit EU-Reverse-Charge ohne USt-IdNr.</strong> —
                meldepflichtig, aber ohne Nummer nicht meldbar:{' '}
                {zm.ohne_ustid.map((o) => `${o.kunde} (${formatMoney(o.netto_cents)})`).join(', ')}.
              </Notice>
            )}
            {zm.zeilen.length === 0 ? (
              <Empty>Keine meldepflichtigen EU-Lieferungen im Zeitraum.</Empty>
            ) : (
              <table style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>USt-IdNr.</th>
                    <th>Kunde</th>
                    <th>Land</th>
                    <th className="num">Belege</th>
                    <th className="num">Netto</th>
                  </tr>
                </thead>
                <tbody>
                  {zm.zeilen.map((z) => (
                    <tr key={z.vat_id}>
                      <td className="mono">{z.vat_id}</td>
                      <td className="small">{z.kunde}</td>
                      <td className="small muted">{z.land}</td>
                      <td className="num">{z.belege}</td>
                      <td className="num">{formatMoney(z.netto_cents)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>
                      Summe
                    </td>
                    <td className="num" style={{ fontWeight: 600 }}>
                      {formatMoney(zm.summe_cents)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </>
        )}
      </Card>

      <Card title="Debitorenkonten und Steuermerkmale" padded={false}>
        <div className="body small muted" style={{ paddingBottom: 0 }}>
          Ohne Debitorenkonto lässt sich für einen Auftraggeber kein Buchungssatz erzeugen.
          Das Steuermerkmal bestimmt, wie kommende Rechnungen ausgewiesen werden – für
          Reverse Charge ist die USt-IdNr. des Kunden Pflicht.
        </div>
        {partners.length === 0 ? (
          <Empty>Keine Auftraggeber vorhanden.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Auftraggeber</th>
                <th>USt-IdNr.</th>
                <th>Steuermerkmal</th>
                <th className="num">Debitorenkonto</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {partners.map((partner) => (
                <tr key={partner.id}>
                  <td>{partner.name}</td>
                  <td style={{ width: 180 }}>
                    <input
                      className="mono"
                      value={vatIds[partner.id] ?? partner.vat_id ?? ''}
                      onChange={(e) => setVatIds({ ...vatIds, [partner.id]: e.target.value })}
                    />
                  </td>
                  <td style={{ width: 200 }}>
                    <select
                      value={treatments[partner.id] ?? partner.tax_treatment}
                      onChange={(e) =>
                        setTreatments({ ...treatments, [partner.id]: e.target.value as TaxTreatment })
                      }
                    >
                      {(Object.keys(TAX_TREATMENT_LABEL) as TaxTreatment[]).map((key) => (
                        <option key={key} value={key}>
                          {TAX_TREATMENT_LABEL[key]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="num" style={{ width: 150 }}>
                    <input
                      style={{ textAlign: 'right' }}
                      value={accounts[partner.id] ?? String(partner.debitor_account ?? '')}
                      onChange={(e) => setAccounts({ ...accounts, [partner.id]: e.target.value })}
                    />
                  </td>
                  <td className="num">
                    <button className="small ghost" onClick={() => saveAccount(partner)} disabled={busy}>
                      speichern
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Einstellungen">
        <strong className="small">DATEV</strong>
        <div className="form-grid" style={{ marginTop: 8 }}>
          <TextField
            label="Beraternummer"
            value={String(settings.consultant_number ?? '')}
            onChange={(v) => patch({ consultant_number: v ? Number(v) : null })}
          />
          <TextField
            label="Mandantennummer"
            value={String(settings.client_number ?? '')}
            onChange={(v) => patch({ client_number: v ? Number(v) : null })}
          />
          <TextField
            label="Erlöskonto"
            value={String(settings.revenue_account ?? '')}
            onChange={(v) => patch({ revenue_account: v ? Number(v) : null })}
            hint="SKR03 üblich 8400, SKR04 üblich 4400"
          />
          <TextField
            label="Erlöskonto 7 %"
            value={String(settings.revenue_account_reduced ?? '')}
            onChange={(v) => patch({ revenue_account_reduced: v ? Number(v) : null })}
            hint="ermäßigt — SKR03 8300, SKR04 4300"
          />
          <TextField
            label="Erlöskonto steuerfrei"
            value={String(settings.revenue_account_free ?? '')}
            onChange={(v) => patch({ revenue_account_free: v ? Number(v) : null })}
            hint="0 % — nur falls im Laden geführt"
          />
          <TextField
            label="Kassenkonto"
            value={String(settings.cash_account ?? '')}
            onChange={(v) => patch({ cash_account: v ? Number(v) : null })}
            hint="für die Bar-Tageslosung — SKR03 1000, SKR04 1600"
          />
          <TextField
            label="Geldtransitkonto"
            value={String(settings.card_transit_account ?? '')}
            onChange={(v) => patch({ card_transit_account: v ? Number(v) : null })}
            hint="für Kartenumsätze — SKR03 1360, SKR04 1460"
          />
          <label className="field">
            <span>Kontenrahmen</span>
            <select value={settings.skr} onChange={(e) => patch({ skr: e.target.value as '03' | '04' })}>
              <option value="03">SKR03</option>
              <option value="04">SKR04</option>
            </select>
          </label>
          <TextField
            label="Sachkontenlänge"
            value={String(settings.account_length)}
            onChange={(v) => patch({ account_length: Number(v) || 4 })}
          />
          <TextField
            label="BU-Schlüssel"
            value={settings.bu_schluessel ?? ''}
            onChange={(v) => patch({ bu_schluessel: v || null })}
            hint="leer lassen bei Automatikkonto"
          />
          <TextField
            label="Steuersatz (%)"
            value={String(settings.tax_rate_percent)}
            onChange={(v) => patch({ tax_rate_percent: Number(v.replace(',', '.')) || 0 })}
          />
          <TextField
            label="Zahlungsziel (Tage)"
            value={String(settings.payment_terms_days)}
            onChange={(v) => patch({ payment_terms_days: Number(v) || 0 })}
          />
        </div>
        <div style={{ marginTop: 8 }}>
          <CheckField
            label="Kleinunternehmer nach § 19 UStG"
            checked={settings.small_business}
            onChange={(v) => patch({ small_business: v })}
          />
          <div className="small muted">
            Dann weist keine Rechnung Umsatzsteuer aus; stattdessen erscheint der Hinweis auf
            § 19 UStG. Gilt ab der nächsten Rechnung – bereits festgeschriebene bleiben
            unverändert.
          </div>
        </div>

        <strong className="small">Eigene Firmendaten — Grundlage der E-Rechnung</strong>
        <div className="form-grid" style={{ marginTop: 8 }}>
          <TextField
            label="Firmenname"
            value={settings.company_name ?? ''}
            onChange={(v) => patch({ company_name: v || null })}
          />
          <TextField label="Strasse" value={settings.street ?? ''} onChange={(v) => patch({ street: v || null })} />
          <TextField label="PLZ" value={settings.zip ?? ''} onChange={(v) => patch({ zip: v || null })} />
          <TextField label="Ort" value={settings.city ?? ''} onChange={(v) => patch({ city: v || null })} />
          <TextField
            label="USt-IdNr."
            value={settings.vat_id ?? ''}
            onChange={(v) => patch({ vat_id: v || null })}
          />
          <TextField
            label="Steuernummer"
            value={settings.tax_number ?? ''}
            onChange={(v) => patch({ tax_number: v || null })}
            hint="falls keine USt-IdNr. vorhanden"
          />
          <TextField label="E-Mail" value={settings.email ?? ''} onChange={(v) => patch({ email: v || null })} />
          <TextField label="IBAN" value={settings.iban ?? ''} onChange={(v) => patch({ iban: v || null })} />
          <TextField label="BIC" value={settings.bic ?? ''} onChange={(v) => patch({ bic: v || null })} />
          <TextField
            label="Bank"
            value={settings.bank_name ?? ''}
            onChange={(v) => patch({ bank_name: v || null })}
          />
        </div>

        <button className="primary" onClick={saveSettings} disabled={busy}>
          {busy ? 'Speichern …' : 'Einstellungen speichern'}
        </button>
      </Card>

      <Card title="Bisherige Übergaben" padded={false}>
        {exports.length === 0 ? (
          <Empty>Noch nichts an DATEV übergeben.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Erzeugt</th>
                <th>Zeitraum</th>
                <th>Datei</th>
                <th className="num">Belege</th>
                <th className="num">Summe</th>
                <th>Durch</th>
              </tr>
            </thead>
            <tbody>
              {exports.map((entry) => (
                <tr key={entry.id}>
                  <td className="small muted">{formatDateTime(entry.created_at)}</td>
                  <td className="small">
                    {formatDate(entry.period_start)} – {formatDate(entry.period_end)}
                  </td>
                  <td className="small mono">{entry.filename}</td>
                  <td className="num">{entry.invoice_count}</td>
                  <td className="num">{formatMoney(entry.total_cents)}</td>
                  <td className="small">{entry.created_by_name ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <Card title="Belegarchiv" padded={false}>
        <div className="body small muted" style={{ paddingBottom: 0 }}>
          Jede erzeugte E-Rechnung wird hier unveränderlich abgelegt. Ein zweiter Download
          liefert exakt dieselbe Datei – die Prüfsumme belegt das.
        </div>
        {archive.length === 0 ? (
          <Empty>Noch keine Belege archiviert.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Erzeugt</th>
                <th>Art</th>
                <th>Beleg</th>
                <th>Datei</th>
                <th className="num">Größe</th>
                <th>Prüfsumme</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {archive.map((doc) => (
                <tr key={doc.id}>
                  <td className="small muted">{formatDateTime(doc.created_at)}</td>
                  <td className="small">{doc.doc_type}</td>
                  <td className="small mono">{doc.number ?? '—'}</td>
                  <td className="small mono">{doc.filename}</td>
                  <td className="num small">{(doc.size_bytes / 1024).toFixed(1)} kB</td>
                  <td className="small mono" title={doc.sha256}>
                    {doc.sha256.slice(0, 12)}…
                  </td>
                  <td className="num">
                    <button
                      className="small ghost"
                      onClick={() => downloadFile(`/api/accounting/archive/${doc.id}`)}
                    >
                      laden
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
